/**
 * api.ts — TanStack Query hooks for the orders feature.
 *
 * BE endpoints:
 *   GET    /orders               → list (limit/offset + filters)
 *   GET    /orders/{id}          → single order
 *   POST   /orders               → create
 *   PUT    /orders/{id}          → patch (all fields except status)
 *   DELETE /orders/{id}          → delete
 *   POST   /orders/{id}/status   → transition status
 *   POST   /orders/{id}/arrived  → khách đã tới cơ sở (máy chủ tự chọn đường đi)
 *   GET    /orders/attention-flags → ba cờ chú ý theo lô id
 *   GET    /orders/{id}/payments → list payments for an order
 *   POST   /orders/{id}/payments → add payment
 *   GET    /orders/{id}/notes    → list notes
 *   POST   /orders/{id}/notes    → add note
 *   PUT    /notes/{noteId}       → edit note (author or privileged role)
 *   DELETE /notes/{noteId}       → delete note (author or privileged role)
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage, serverRawMessage } from "@/lib/api/error-message";
import {
  COSMETIC_LINES_LIVE,
  CosmeticLinesLiveError,
  type CosmeticDisposition,
} from "./cosmetics";
import type { components } from "@/lib/api/generated/schema";
import { customerKeys } from "@/features/customers/api";
import { CATALOG_KEY } from "@/features/products/catalogApi";
import { scheduleKeys } from "@/features/schedule/queryKeys";
import type { OrderInputValues } from "./schemas";
import type { OverrideReasonCode } from "@/features/order-dispatch/overrideReasons";
import { useCanApproveSurcharge } from "./surchargePermissions";

export type Order = components["schemas"]["Order"];
export type OrderSummary = components["schemas"]["OrderSummary"];
export type OrderSummaryPage = components["schemas"]["OrderSummaryPage"];
export type OrderStatusCounts = components["schemas"]["OrderStatusCounts"];
export type Payment = components["schemas"]["Payment"];
export type Surcharge = components["schemas"]["Surcharge"];
export type SurchargeInput = components["schemas"]["SurchargeInput"];
export type SurchargePendingItem = components["schemas"]["SurchargePendingItem"];
export type OrderNote = components["schemas"]["OrderNote"];
export type OrderNoteInput = components["schemas"]["OrderNoteInput"];
export type OrderNoteReaction = components["schemas"]["OrderNoteReaction"];
export type OrderNoteMentionCandidate = components["schemas"]["OrderNoteMentionCandidate"];
/** Bốn cảm xúc thả được lên ghi chú đơn — khớp CHECK của migration 0420. */
export const NOTE_REACTIONS = ["👍", "✅", "❤️", "😮"] as const;
export type NoteReactionEmoji = (typeof NOTE_REACTIONS)[number];
export type NoteUpdateInput = components["schemas"]["NoteUpdateInput"];
export type AuditEntry = components["schemas"]["AuditEntry"];
type OrderInput = components["schemas"]["OrderInput"];
type OrderPatch = components["schemas"]["OrderPatch"];
type OrderStatusTransition = components["schemas"]["OrderStatusTransition"];

// ══════════ QUERY KEYS ══════════

export const orderKeys = {
  all: ["orders"] as const,
  list: (params?: { limit?: number; offset?: number }) => ["orders", "list", params ?? {}] as const,
  summaries: (params: Record<string, string | number | boolean | undefined>) =>
    ["orders", "summaries", params] as const,
  statusCounts: (params: Record<string, string | number | boolean | undefined>) =>
    ["orders", "status-counts", params] as const,
  detail: (id: string) => ["orders", "detail", id] as const,
  // NẰM DƯỚI khoá `detail`: mọi thao tác đã làm tươi chi tiết đơn (phát sinh,
  // thu tiền, nâng gói, sửa đơn) tự làm tươi luôn thẻ Quyền lợi ảnh.
  photoEntitlements: (id: string) => ["orders", "detail", id, "photo-entitlements"] as const,
  // CHUNG khoá với usePaymentsByOrder (features/payments/api.ts): hai hook cùng
  // đọc GET /orders/{id}/payments. Hai khoá riêng là nút thu tiền làm mới một
  // bên, thẻ Thanh toán của đơn đứng im tới khi tải lại (kiểm thử 17/10, T03).
  payments: (orderId: string) => ["order", orderId, "payments"] as const,
  invoiceIssueResults: (orderId: string) => ["order", orderId, "invoice-issue-results"] as const,
  paymentsBatch: (orderIds: string[]) =>
    ["orders", "payments-batch", [...orderIds].sort()] as const,
  surcharges: (orderId: string) => ["orders", orderId, "surcharges"] as const,
  notes: (orderId: string) => ["orders", orderId, "notes"] as const,
  // Ghi chú của MỘT tấm (QĐ 0438) — nằm DƯỚI khoá `notes` nên mọi lượt làm mới
  // sổ chung (thêm / sửa / xoá / cảm xúc) làm tươi luôn khối của tấm.
  assetNotes: (orderId: string, assetId: string) => ["orders", orderId, "notes", "asset", assetId] as const,
  noteMentionCandidates: (orderId: string, q: string) =>
    ["orders", orderId, "note-mention-candidates", q] as const,
  audit: (orderId: string) => ["orders", orderId, "audit"] as const,
  packageUpgrades: (orderId: string) => ["orders", orderId, "package-upgrades"] as const,
  packageUpgradeOptions: (orderId: string) =>
    ["orders", orderId, "package-upgrade-options"] as const,
  packageUpgradeRequests: (orderId: string) => ["orders", orderId, "package-upgrade-requests"] as const,
  makeupTierPackages: ["orders", "makeup-tier-packages"] as const,
};

/**
 * Khoá cache hàng chờ duyệt phát sinh XUYÊN ĐƠN — nằm ngoài orderKeys vì nó
 * không thuộc đơn nào: một danh sách gom mọi đơn trong scope chi nhánh.
 */
export const surchargePendingKey = ["surcharges", "pending"] as const;

/**
 * Huỷ hiệu lực cache sau khi một đơn hàng đổi.
 *
 * Quét cả lưới Lịch chụp chứ không chỉ danh sách đơn: lưới đọc `/schedule`
 * (một endpoint khác, khoá cache khác) nhưng vẽ ra chính những đơn này. Đổi
 * ngày/giờ, phân công ekip, huỷ hay xoá đơn đều làm lưới sai ngay lập tức —
 * trước khi có dòng này, người dùng phải tải lại trang mới thấy lịch mới.
 *
 * Chỉ quét gốc `scheduleKeys.all`: khoá của lưới mang theo khoảng ngày và chi
 * nhánh đang xem, mà đổi lịch có thể chuyển đơn sang một khoảng khác, nên
 * không thể suy ra riêng khoảng nào cần làm mới.
 *
 * @param orderId khi có, làm mới luôn màn chi tiết của đúng đơn đó
 */
function invalidateOrderAndSchedule(qc: QueryClient, orderId?: string): void {
  void qc.invalidateQueries({ queryKey: orderKeys.all });
  if (orderId) void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
  void qc.invalidateQueries({ queryKey: scheduleKeys.all });
}

// ══════════ LIST ══════════

export function useOrders(limit = 50, offset = 0, enabled = true) {
  return useQuery({
    queryKey: orderKeys.list({ limit, offset }),
    queryFn: async () => {
      const { data, error } = await api.GET("/orders", {
        params: { query: { limit, offset } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách đơn hàng"));
      return data;
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

// ══════════ LIGHT LIST (server-paging) ══════════

export interface SummaryQuery {
  status?: string; // omit for all; pass "completed" etc.
  hideDone?: boolean; // mirrors FE "Tất cả" tab → hide_done=1
  open?: boolean; // restrict to open-for-collection set (PaymentCollectTab)
  hasDeposit?: boolean;
  // hasPendingPayment — chỉ đơn đang có phiếu thu chờ duyệt (thẻ con "Đang chờ
  // thanh toán" của tab Cần thu). Phải lọc ở máy chủ: danh sách phân trang 20
  // đơn một lượt nên lọc ở giao diện chỉ bắt được số đơn chờ lọt vào trang
  // đang xem.
  hasPendingPayment?: boolean;
  noPhone?: boolean; // restrict to orders missing a phone (tab "Chưa có SĐT")
  postponed?: boolean; // restrict to orders tạm hoãn vô thời hạn (tab "Tạm hoãn")
  // restrict to đơn chưa có ngày chụp, trừ đơn tạm hoãn (thẻ "Thiếu ngày chụp")
  missingShootDate?: boolean;
  reshoot?: boolean; // restrict to đơn chụp bù sau khiếu nại (thẻ "Chụp bù")
  // missingInvoice — ô tích "🧾 Chưa nhập hoá đơn": đơn chưa chọn cách xuất
  // hoá đơn, hoặc hoá đơn cá nhân chưa có tên thật. Máy chủ trả 403 cho người
  // không xem được tiền đơn, nên chỉ gửi khi ô hiện cho người đó.
  missingInvoice?: boolean;
  // Hai thẻ KHÉP ĐƠN (QĐ 0292): "open" = đã giao chưa khép (treo lâu nhất trước),
  // "closed" = đã khép. Cắt TRONG ô "Đã giao", nên KHÔNG gửi kèm `status`.
  closure?: "open" | "closed";
  category?: string;
  // brand — mã thương hiệu của ĐƠN (orders.brand_code), khớp chính xác. Ô
  // "Thương hiệu" trên màn Đơn hàng truyền vào đây; thiếu trường này thì tham
  // số bị nuốt IM LẶNG vì OrdersListView truyền bằng spread (TypeScript không
  // áp excess-property check cho spread), và ô lọc trông như hỏng.
  brand?: string;
  branch?: string;
  saleId?: string;
  // staffId — khớp nhân sự ở BẤT KỲ vai nào trên đơn (bán/chụp/trang
  // điểm/hậu kỳ, kể cả ê-kíp phụ), khác hẳn `saleId` vốn chỉ soi cột người
  // bán. Máy chủ AND nó với phạm vi của chính người gọi, nên truyền id người
  // khác chỉ làm kết quả HẸP đi chứ không bao giờ nới ra.
  staffId?: string;
  // customerId — chỉ đơn của một khách trong danh bạ (trang hồ sơ khách). Máy
  // chủ khớp theo 9 số cuối điện thoại và trả 403 cho người không được thấy số.
  customerId?: string;
  q?: string;
  tag?: string; // containment filter on order.tags (server-side)
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  // dateBasis — trục mà dateFrom/dateTo so vào. Vắng = ngày chụp (mọi màn
  // cũ). "created" = ngày tạo đơn; "pending_payment" = ngày lập phiếu chờ —
  // hai trục của thẻ Thu tiền trên trang Thanh toán.
  dateBasis?: "shoot" | "created" | "pending_payment";
  smartSort?: boolean; // default true
  limit: number;
  offset: number;
}

/**
 * useOrderSummaries — server-side filter + sort + paginate over the
 * light-projection /orders/summaries endpoint. Use this for the orders-list
 * view; full Order rows go through useOrder(id) for the detail page.
 */
export function useOrderSummaries(qs: SummaryQuery, opts?: { enabled?: boolean }) {
  const params: Record<string, string | number> = {
    limit: qs.limit,
    offset: qs.offset,
  };
  if (qs.status) params["status"] = qs.status;
  if (qs.hideDone) params["hide_done"] = "1";
  if (qs.open) params["open"] = "1";
  if (qs.hasDeposit) params["has_deposit"] = "1";
  if (qs.hasPendingPayment) params["has_pending_payment"] = "1";
  if (qs.noPhone) params["no_phone"] = "1";
  if (qs.postponed) params["postponed"] = "1";
  if (qs.missingShootDate) params["missing_shoot_date"] = "1";
  if (qs.reshoot) params["reshoot"] = "1";
  if (qs.missingInvoice) params["missing_invoice"] = "1";
  if (qs.closure) params["closure"] = qs.closure;
  if (qs.category) params["category"] = qs.category;
  if (qs.brand) params["brand"] = qs.brand;
  if (qs.branch) params["branch"] = qs.branch;
  if (qs.saleId) params["sale_id"] = qs.saleId;
  if (qs.staffId) params["staff_id"] = qs.staffId;
  if (qs.customerId) params["customer_id"] = qs.customerId;
  if (qs.q) params["q"] = qs.q;
  if (qs.tag) params["tag"] = qs.tag;
  if (qs.dateFrom) params["date_from"] = qs.dateFrom;
  if (qs.dateTo) params["date_to"] = qs.dateTo;
  if (qs.dateBasis) params["date_basis"] = qs.dateBasis;
  if (qs.smartSort === false) params["smart_sort"] = "0";

  return useQuery({
    queryKey: orderKeys.summaries(params),
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/summaries", {
        params: { query: params },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách đơn hàng"));
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

// ══════════ STATUS COUNTS ══════════

// dateBasis bị bỏ: /orders/status-counts luôn lọc theo ngày chụp, nhận trường
// này là nuốt nó im lặng.
export type StatusCountsQuery = Omit<
  SummaryQuery,
  "status" | "limit" | "offset" | "smartSort" | "dateBasis"
>;

/**
 * useOrderStatusCounts — count theo từng status (cho badge bên cạnh tab) +
 * "chưa cọc" + total. Áp filter chung NHƯNG bỏ qua `status` để FE render
 * cùng lúc tất cả tab.
 */
export function useOrderStatusCounts(qs: StatusCountsQuery) {
  const params: Record<string, string | number> = {};
  if (qs.hideDone) params["hide_done"] = "1";
  if (qs.missingInvoice) params["missing_invoice"] = "1";
  if (qs.category) params["category"] = qs.category;
  // Badge phải đếm CÙNG tập mà danh sách hiện — quên brand ở đây là số trên
  // thẻ nói một đằng, danh sách bên dưới một nẻo.
  if (qs.brand) params["brand"] = qs.brand;
  if (qs.branch) params["branch"] = qs.branch;
  if (qs.saleId) params["sale_id"] = qs.saleId;
  if (qs.q) params["q"] = qs.q;
  if (qs.tag) params["tag"] = qs.tag;
  if (qs.dateFrom) params["date_from"] = qs.dateFrom;
  if (qs.dateTo) params["date_to"] = qs.dateTo;

  return useQuery({
    queryKey: orderKeys.statusCounts(params),
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/status-counts", {
        params: { query: params },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được số đếm theo trạng thái"));
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ══════════ DETAIL ══════════

export function useOrder(id: string | null) {
  return useQuery({
    queryKey: orderKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}", {
        params: { path: { id: id! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đơn hàng"));
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export type OrderPhotoEntitlements = components["schemas"]["OrderPhotoEntitlements"];

/**
 * Quyền lợi ảnh của đơn — số ảnh chỉnh / ảnh in và từng nguồn. Máy chủ tính
 * bằng đúng công thức của cổng chặn giao ảnh; màn chỉ HIỆN, không tự cộng.
 */
export function useOrderPhotoEntitlements(id: string | null | undefined) {
  return useQuery({
    queryKey: orderKeys.photoEntitlements(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/photo-entitlements", {
        params: { path: { id: id! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được quyền lợi ảnh của đơn"));
      return data;
    },
    staleTime: 30_000,
  });
}

// ══════════ DATE HELPERS ══════════

const DATE_FIELDS = ["shootDate", "bookDate", "deliveryDate"] as const;

function dateToRFC3339(v: unknown): string | undefined {
  if (typeof v !== "string" || !v) return undefined;
  if (v.includes("T")) return v;
  return `${v}T00:00:00Z`;
}

function normalizeDates<T extends Record<string, unknown>>(obj: T): T {
  const out = { ...obj };
  for (const key of DATE_FIELDS) {
    if (key in out) {
      const converted = dateToRFC3339(out[key]);
      if (converted) {
        (out as Record<string, unknown>)[key] = converted;
      } else {
        delete (out as Record<string, unknown>)[key];
      }
    }
  }
  return out;
}

// ══════════ CREATE ══════════

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    // `quote` đi riêng ngoài lược đồ form: đơn của dòng bán theo báo giá gửi
    // kèm bản báo giá soạn ở khối Báo giá (QĐ 0178).
    mutationFn: async (input: OrderInputValues & { quote?: OrderInput["quote"]; halfDaySlotId?: string }) => {
      const { data, error } = await api.POST("/orders", {
        body: normalizeDates(input) as OrderInput,
      });
      if (error || !data) {
        // Surface the BE message (e.g. đóng ca conflict) when present.
        throw new Error(serverErrorMessage(error, "Không tạo được đơn hàng"));
      }
      return data;
    },
    onSuccess: () => {
      invalidateOrderAndSchedule(qc);
      // Máy chủ vừa tạo / cập nhật hồ sơ khách theo SĐT của đơn (lỗi #20 kiểm
      // thử 16/09/2026): trang Khách hàng và dòng "Hồ sơ KH" của đơn phải đọc
      // lại, không giữ câu "chưa có hồ sơ" form vừa tra lúc gõ SĐT.
      void qc.invalidateQueries({ queryKey: customerKeys.all });
    },
    // Máy chủ đối chiếu giá với bảng giá (lỗi #02 kiểm thử 16/09/2026). Bảng giá
    // vừa đổi mà form còn cầm bản cũ thì bị từ chối kèm giá mới — tải lại danh
    // mục để lần chọn lại gói lấy đúng giá, không phải tải lại cả trang.
    onError: () => void qc.invalidateQueries({ queryKey: CATALOG_KEY }),
  });
}

// ══════════ CHUYỂN THANH TOÁN TỪ ĐƠN ĐÃ HUỶ ══════════

export type TransferableOrder = components["schemas"]["TransferableOrder"];

/**
 * Đơn đã huỷ còn giữ tiền của khách, để chọn khi tạo đơn mới (migration 0151).
 *
 * Chỉ hỏi khi số điện thoại đã đủ 9 chữ số — máy chủ so khớp bằng 9 số cuối và
 * trả rỗng cho số ngắn hơn, nên hỏi sớm chỉ tốn lượt gọi. Truyền số ĐÃ LẮNG
 * (debounced) từ chỗ gọi; ô SĐT gõ từng ký tự.
 */
export function useTransferableOrders(phone: string) {
  const digits = phone.replace(/\D/g, "");
  const enabled = digits.length >= 9;
  return useQuery({
    queryKey: ["orders", "transferable", digits.slice(-9)],
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/transferable-payments", {
        params: { query: { phone } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không tra được đơn cũ còn tiền"));
      return data ?? [];
    },
  });
}

// ══════════ HẠNG TRANG ĐIỂM THEO GÓI ══════════

export type MakeupTierPackages = components["schemas"]["OrderMakeupTierPackages"];

/**
 * Hai danh sách gói "trang điểm cơ bản / cao cấp" của thẻ Makeup Profile — để ô
 * Hạng trang điểm trên đơn điền sẵn theo gói (quyết định 0177).
 *
 * Nguồn DUY NHẤT là máy chủ: đúng hai danh sách máy lương đọc lúc trả tiền. Chép
 * danh sách gói xuống giao diện là kế toán xếp lại hạng một gói mà ô vẫn nói
 * hạng cũ.
 *
 * `enabled` = dòng của gói đang chọn có chia hạng trang điểm; đơn ảnh thẻ và
 * các dòng khác không hỏi. Lỗi thì `data` để `undefined` — chỗ dùng coi là "chưa
 * đọc được" và KHÔNG bắt sale chọn tay (máy chủ vẫn gác).
 */
export function useMakeupTierPackages(enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.makeupTierPackages,
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<MakeupTierPackages> => {
      const { data, error } = await api.GET("/orders/makeup-tier-packages");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được hạng trang điểm theo gói"));
      return data;
    },
  });
}

// ══════════ PATCH ══════════

export function usePatchOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: OrderPatch }) => {
      const { data, error } = await api.PUT("/orders/{id}", {
        params: { path: { id } },
        body: normalizeDates(input) as OrderPatch,
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không cập nhật được đơn hàng"));
      }
      return data;
    },
    onSuccess: (_data, vars) => {
      // Cả sổ công: khối "Chia việc trang điểm" đọc số nữ / nam / bé của đơn từ
      // đó — khai số người trang điểm xong mà không làm tươi là khối vẫn báo
      // "chưa khai" tới khi tải lại trang.
      invalidateEkipChange(qc, vars.id);
      // Sửa SĐT / đơn chưa gắn hồ sơ → máy chủ gắn lại hồ sơ khách (lỗi #20).
      void qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

// ══════════ EKIP — thêm/gỡ MỘT người ══════════
//
// Hai hook dưới đây gửi DELTA, không gửi cả mảng. Lý do (quyết định 0121):
// `members` mà màn hình đang giữ có thể đã cũ tới 30 giây (staleTime bên
// dưới), nên dựng lại cả mảng từ nó rồi gửi đi là ghi đè mất người mà ai đó
// vừa thêm — không lỗi, không cảnh báo, chỉ mất.
//
// Gửi delta thì không có gì để mà cũ: hai người ngồi hai máy cùng thêm hai
// người khác nhau vào cùng một vai thì cả hai cùng vào.

export type EkipRole = "photographer" | "makeup" | "retouch";

export function useAddEkipMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      role,
      staffId,
      staffName,
      certExceptionReason,
    }: {
      orderId: string;
      role: EkipRole;
      staffId: string;
      staffName?: string;
      certExceptionReason?: string;
    }) => {
      const { data, error } = await api.POST("/orders/{id}/ekip/{role}/members", {
        params: { path: { id: orderId, role } },
        body: {
          staffId,
          ...(staffName ? { staffName } : {}),
          ...(certExceptionReason ? { certExceptionReason } : {}),
        },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không thêm được người vào ekip"));
      }
      return data;
    },
    onSuccess: (_data, vars) => invalidateEkipChange(qc, vars.orderId),
  });
}

export function useRemoveEkipMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      role,
      staffId,
      reasonCode,
      reasonNote,
    }: {
      orderId: string;
      role: EkipRole;
      staffId: string;
      /** Lý do gỡ (QĐ 0424) — máy chủ bắt buộc với thợ chụp / trang điểm. */
      reasonCode?: OverrideReasonCode;
      reasonNote?: string;
    }) => {
      const { data, error } = await api.DELETE("/orders/{id}/ekip/{role}/members/{staffId}", {
        params: {
          path: { id: orderId, role, staffId },
          query: {
            ...(reasonCode ? { reasonCode } : {}),
            ...(reasonNote ? { reasonNote } : {}),
          },
        },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không gỡ được người khỏi ekip"));
      }
      return data;
    },
    onSuccess: (_data, vars) => invalidateEkipChange(qc, vars.orderId),
  });
}

/**
 * Thêm / gỡ người trong ekip đổi luôn sổ công của buổi: khối "Chia việc trang
 * điểm" đọc danh sách thợ từ `/orders/:id/work-entries` (khoá riêng, không nằm
 * dưới `orderKeys.all`). Thiếu dòng này thì gán thợ makeup xong, chip đã hiện
 * mà khối chia việc vẫn giữ danh sách cũ tới khi tải lại trang.
 *
 * Gõ thẳng khoá thay vì import `quoteKeys`: `quote/api.ts` đã import tệp này,
 * import ngược là vòng import (cùng cách `ekip-schedule/api.ts` đang làm).
 */
function invalidateEkipChange(qc: QueryClient, orderId: string): void {
  invalidateOrderAndSchedule(qc, orderId);
  void qc.invalidateQueries({ queryKey: ["quote", "work-entries", orderId] });
}

// ══════════ DELETE ══════════

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/orders/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không xóa được đơn hàng"));
    },
    onSuccess: () => invalidateOrderAndSchedule(qc),
  });
}

// ══════════ STATUS TRANSITION ══════════

export function useTransitionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      override,
      overrideReason,
      cancelReason,
      quoteLostReason,
    }: {
      id: string;
      status: OrderStatusTransition["status"];
      /** Cửa thoát cho sếp: bỏ qua bảng quyền + rào ảnh thẻ. Bắt buộc kèm lý do. */
      override?: boolean;
      overrideReason?: string;
      /**
       * Lý do huỷ (chỉ có nghĩa khi status = cancelled). Kiểm toán 05/09/2026,
       * OD-UI-04: ô "Lý do hủy" từng thu chữ rồi bỏ đi — máy chủ nay ghi nó
       * vào sổ trạng thái cùng entry huỷ.
       */
      cancelReason?: string;
      /**
       * Lý do khách không chốt — máy chủ bắt buộc khi huỷ hợp đồng báo giá còn
       * "giữ chỗ tạm" (QĐ 0178 §9). Trước 17/10/2026 màn không gửi nên không
       * huỷ được hợp đồng đó (kiểm thử đóng vai, D05).
       */
      quoteLostReason?: string;
    }) => {
      const { data, error } = await api.POST("/orders/{id}/status", {
        params: { path: { id } },
        // Lượt thường KHÔNG gửi hai field này: máy chủ đòi lý do mỗi khi thấy
        // cờ, nên gửi kèm `override: false` là tự chuốc lỗi 400 cho đường đi
        // phổ biến nhất.
        body: {
          status,
          ...(override ? { override, overrideReason } : {}),
          ...(cancelReason ? { cancelReason } : {}),
          ...(quoteLostReason ? { quoteLostReason } : {}),
        },
      });
      if (error || !data) {
        // Giống useCreateOrder/usePatchOrder ngay trên: giữ nguyên câu máy chủ
        // đã viết. Riêng đơn ảnh thẻ, máy chủ từ chối đóng đơn bằng mã
        // `photo_acceptance_required` kèm câu giải thích khách còn thiếu gì —
        // nuốt mất câu đó thì nhân viên chỉ thấy "thất bại" rồi bấm lại mãi mà
        // không hiểu vì sao.
        //
        // KHÔNG chép câu đó ra đây: câu chữ bên máy chủ đã đổi một lần
        // (24/08/2026, Đ11 — cổng hạ từ "khách chưa ký" xuống "khách chưa chốt
        // xong bản sửa"), và bản sao ở FE thì không có gì bắt lỗi khi nó lỗi
        // thời. Chỗ này chỉ có một việc: đưa lời máy chủ qua serverErrorMessage
        // (tiếng Việt giữ nguyên văn, tiếng Anh dịch — không bao giờ hiện thô).
        // Huỷ đơn còn mỹ phẩm đã giao (QĐ 0260): màn cần biết đúng ca này để
        // mở hộp liệt kê dòng mỹ phẩm — nhận theo mã máy, không so câu chữ.
        if (serverRawMessage(error).includes(COSMETIC_LINES_LIVE)) {
          throw new CosmeticLinesLiveError();
        }
        throw new Error(serverErrorMessage(error, "Chuyển trạng thái thất bại"));
      }
      return data;
    },
    onSuccess: (_data, vars) => {
      invalidateOrderAndSchedule(qc, vars.id);
      if (vars.status === "cancelled") {
        qc.invalidateQueries({ queryKey: ["payments"] });
      }
    },
  });
}

/**
 * Nút "Khách đã tới" — ghi nhận khách có mặt tại cơ sở.
 *
 * KHÔNG gửi thân yêu cầu: đơn đi đâu là việc của máy chủ, không phải của màn
 * hình. Đơn chụp profile sang thẳng "đang chụp" (`mode: transitioned`); đơn ảnh
 * thẻ vào bảng điều phối chờ tới lượt (`mode: queued`) và chính bảng ấy mới đẩy
 * đơn đi. Màn hình phải đọc `mode` để nói đúng việc vừa xảy ra — báo nhầm là
 * nói dối người dùng về trạng thái đơn.
 */
export function useCustomerArrived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data, error } = await api.POST("/orders/{id}/arrived", {
        params: { path: { id } },
      });
      if (error || !data) {
        // Giữ nguyên câu máy chủ đã viết (giống useTransitionOrder ngay trên):
        // máy chủ từ chối kèm lý do cụ thể — "chưa tới ngày chụp", "đơn đã đóng"
        // — nuốt mất thì người dùng chỉ thấy "thất bại" rồi bấm lại mãi.
        throw new Error(serverErrorMessage(error, "Ghi nhận khách tới thất bại"));
      }
      return data;
    },
    onSuccess: (_data, vars) => invalidateOrderAndSchedule(qc, vars.id),
  });
}

/**
 * Hậu kiểm ảnh gốc — chấm Đạt / Không đạt (CEO chốt 25/08/2026).
 *
 * Đây là đường DUY NHẤT tắt được nhãn vàng "Chưa hậu kiểm": máy chủ ghi
 * `raw_photo_approved` sang approved/rejected, và cờ `postCheckPending` hỏi
 * "đã có ai soi chưa" nên CẢ HAI kết quả đều tắt nhãn.
 *
 * `invalidateOrderAndSchedule` đủ để nhãn tự cập nhật: khoá của cờ chú ý nằm
 * DƯỚI `orderKeys.all` (`[...orderKeys.all, "attention-flags", …]`), nên lượt
 * dọn theo tiền tố quét luôn cả nó. Không hiển nhiên khi đọc lướt — đừng đổi
 * khoá đó ra ngoài `orderKeys.all` mà không sửa chỗ này.
 */
export function useReviewRawPhotos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      decision,
      note,
      onlyIfPending,
    }: {
      id: string;
      decision: "approved" | "rejected";
      note?: string;
      /** Bàn làm việc QC: đơn đã có người chấm thì máy chủ trả 409, không đè. */
      onlyIfPending?: boolean;
    }) => {
      const { data, error } = await api.POST("/orders/{id}/raw-photos/review", {
        params: { path: { id } },
        body: { decision, ...(note ? { note } : {}), ...(onlyIfPending ? { onlyIfPending } : {}) },
      });
      if (error || !data) {
        // Giữ nguyên câu máy chủ: nó nói rõ "đơn chưa có ảnh gốc để hậu kiểm"
        // hay "chấm không đạt thì phải ghi lý do" — nuốt đi là người dùng chỉ
        // thấy "thất bại" rồi bấm lại mãi.
        throw new Error(serverErrorMessage(error, "Ghi kết quả hậu kiểm thất bại"));
      }
      return data;
    },
    onSuccess: (_data, vars) => {
      invalidateOrderAndSchedule(qc, vars.id);
      // Hàng đợi hậu kiểm của Bàn làm việc QC: đơn vừa chấm phải rời danh sách.
      void qc.invalidateQueries({ queryKey: ["qc-desk", "raw-review-pending"] });
    },
  });
}

/** Ba cờ chú ý của một lô đơn, khoá theo đúng danh sách id đang hiển thị. */
export type OrderAttentionFlags = Record<
  string,
  {
    makeupProofMissing?: string[];
    debtRemaining: number;
    /** Đơn còn nợ nhưng người xem không được thấy số tiền (QĐ 0132). */
    debtAmountHidden?: boolean;
    postCheckPending: boolean;
    /** Tuổi ở bước hiện tại theo luật máy đi tuần (QĐ 0280 câu 5a). */
    step?: { group: string; days: number; thresholdDays: number; overdue: boolean };
    /** Bảng kiểm KHÉP ĐƠN (QĐ 0292) — chỉ có ở đơn đã giao. */
    closure?: components["schemas"]["OrderClosure"];
  }
>;

/**
 * Cờ chú ý cho cả trang danh sách — MỘT lượt hỏi cho mọi dòng.
 *
 * Hai luật của đường này:
 *  - lô rỗng thì KHÔNG hỏi (trang chưa có dòng nào, hỏi là tốn một lượt vô ích);
 *  - hỏng thì trả về rỗng chứ KHÔNG ném lỗi. Cờ là thứ nhắc thêm, không phải nội
 *    dung chính; để nó ném lỗi là cho một tính năng phụ quyền làm trắng cả bảng
 *    đơn — đúng thứ spec đã từ chối (không cờ nào được chặn việc gì).
 */
export function useOrderAttentionFlags(ids: string[]) {
  const key = [...ids].sort();
  return useQuery({
    queryKey: [...orderKeys.all, "attention-flags", key] as const,
    enabled: key.length > 0,
    queryFn: async (): Promise<OrderAttentionFlags> => {
      const { data, error } = await api.GET("/orders/attention-flags", {
        params: { query: { ids: key.join(",") } },
      });
      if (error || !data) return {};
      return (data.flags ?? {}) as OrderAttentionFlags;
    },
  });
}

// ══════════ PAYMENTS ══════════

export function useOrderPayments(orderId: string | null) {
  return useQuery({
    queryKey: orderKeys.payments(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/payments", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được lịch sử thanh toán"));
      return data;
    },
    staleTime: 30_000,
  });
}

export function useOrderInvoiceIssueResults(orderId: string | null, enabled = true) {
  return useQuery({
    queryKey: orderKeys.invoiceIssueResults(orderId ?? ""),
    enabled: !!orderId && enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/invoice-issue-results", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không tải được trạng thái hóa đơn"));
      }
      return data;
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Batch fetch payments by order IDs. Used by the orders-list "Chưa thu tiền" filter
 * so 200 visible orders consume one round-trip instead of 200.
 * Returns a Record<orderId, Payment[]> with every requested id pre-seeded.
 */
export function usePaymentsBatch(orderIds: string[]) {
  const sorted = [...orderIds].sort();
  return useQuery({
    queryKey: orderKeys.paymentsBatch(sorted),
    enabled: sorted.length > 0,
    queryFn: async () => {
      const { data, error } = await api.GET("/payments", {
        params: { query: { orderIds: sorted.join(",") } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được thanh toán batch"));
      return data as Record<string, Payment[]>;
    },
    staleTime: 30_000,
  });
}

// ══════════ SURCHARGES ══════════

export function useSurcharges(orderId: string | null) {
  return useQuery({
    queryKey: orderKeys.surcharges(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/surcharges", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được phát sinh"));
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * useSurchargesPending — hàng chờ duyệt phát sinh XUYÊN ĐƠN (GET
 * /surcharges/pending, Đợt 2 Task 6 — nợ C final-review Đợt 1: người duyệt
 * trước đây chỉ thấy dòng chờ khi mở ĐÚNG đơn). Gom dòng chờ duyệt + dòng
 * mang yêu cầu xoá trên mọi đơn trong scope chi nhánh, chờ lâu nhất lên đầu.
 * Chỉ chạy cho người giữ `order.surcharge.approve` — người khác BE trả 403
 * nên khỏi gọi cho tốn (enabled).
 */
export function useSurchargesPending() {
  const canApprove = useCanApproveSurcharge();
  return useQuery({
    queryKey: surchargePendingKey,
    enabled: canApprove,
    queryFn: async () => {
      const { data, error } = await api.GET("/surcharges/pending");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hàng chờ duyệt phát sinh"));
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * Mã máy-đọc BE gắn trong câu lỗi 403 khi thợ chụp / trang điểm thêm dòng bán
 * (phát sinh, nâng gói, ảnh phát sinh) vào đơn mình KHÔNG được phân công
 * (QĐ 0132 câu 6a). Cùng khuôn `extra_photo_line_locked`: mã HTTP `code` là
 * "forbidden" chung, nên nhận diện theo dấu này rồi hiện câu CEO duyệt — không
 * để cái đuôi `[sale_line_not_assigned]` lọt lên toast.
 */
export const SALE_LINE_NOT_ASSIGNED = "sale_line_not_assigned";
export const SALE_LINE_NOT_ASSIGNED_MESSAGE = "Chỉ thêm được vào đơn mình được phân công";

/**
 * Câu cho người dùng từ lỗi máy chủ: câu duyệt nếu là rào phân công, còn lại
 * qua bộ dịch chung (tiếng Việt nguyên văn, tiếng Anh dịch, không lý do → `fallback`).
 */
export function saleLineErrorMessage(error: unknown, fallback: string): string {
  if (serverRawMessage(error).includes(SALE_LINE_NOT_ASSIGNED))
    return SALE_LINE_NOT_ASSIGNED_MESSAGE;
  return serverErrorMessage(error, fallback);
}

/**
 * Thêm phát sinh — 2 chế độ khoá giá (spec M1):
 *   bảng giá:  `{ catalogId, qty }` — server tự tra giá, amount client bị bỏ qua;
 *   tự do:     `{ type: "custom", label, amount }` — vào `pending_approval` trừ
 *              khi người gửi có `order.surcharge.approve`.
 * BE từ chối type tự do kiểu cũ, nên surface message BE cho người dùng thấy lý do.
 */
export function useAddSurcharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, input }: { orderId: string; input: SurchargeInput }) => {
      const { data, error } = await api.POST("/orders/{id}/surcharges", {
        params: { path: { id: orderId } },
        body: input,
      });
      if (error || !data) {
        throw new Error(saleLineErrorMessage(error, "Không thêm được phát sinh"));
      }
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.surcharges(vars.orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.orderId) });
    },
  });
}

/**
 * Xoá phát sinh (soft-delete có vết — spec M2). Ngoài rào tự-xoá (quá 10 phút
 * hoặc đơn đã có khoản thu xác nhận) server KHÔNG xoá mà ghi yêu cầu chờ duyệt
 * và trả 202 — caller nhận `{ deleteRequested: true }` để báo đúng sự thật
 * thay vì toast lỗi. `reason` bắt buộc với người giữ `order.surcharge.delete`.
 */
export function useDeleteSurcharge(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
      disposition,
    }: {
      id: string;
      reason?: string;
      /** Bắt buộc với dòng mỹ phẩm đã giao (QĐ 0260): hộp còn nguyên / đã mở. */
      disposition?: CosmeticDisposition;
    }) => {
      const query = {
        ...(reason ? { reason } : {}),
        ...(disposition ? { disposition } : {}),
      };
      const { error, response } = await api.DELETE("/surcharges/{id}", {
        params: { path: { id }, ...(reason || disposition ? { query } : {}) },
      });
      if (error) {
        // Nổi message BE (khuôn T10 như useAddSurcharge): UI cần nhận diện
        // lỗi "cần lý do để gửi yêu cầu xoá" (vá B §M2) để hỏi lý do rồi gọi
        // lại — nuốt message là flow 2 nhịp mù đường.
        throw new Error(serverErrorMessage(error, "Không xoá được phát sinh"));
      }
      return { deleteRequested: response.status === 202 };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      // Xoá rút dòng khỏi hàng chờ; 202 thì NGƯỢC LẠI thêm dòng chờ-xoá vào.
      qc.invalidateQueries({ queryKey: surchargePendingKey });
    },
  });
}

/** Duyệt dòng chờ duyệt (pending_approval → active). Cần `order.surcharge.approve`. */
export function useApproveSurcharge(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.POST("/surcharges/{id}/approve", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không duyệt được phát sinh"));
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      // Dòng vừa duyệt phải biến khỏi hàng chờ xuyên đơn ngay (Task 6 Đợt 2).
      qc.invalidateQueries({ queryKey: surchargePendingKey });
    },
  });
}

/** Từ chối dòng chờ duyệt (pending_approval → rejected, không tính tiền). */
export function useRejectSurcharge(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { error } = await api.POST("/surcharges/{id}/reject", {
        params: { path: { id } },
        body: reason ? { reason } : {},
      });
      if (error) throw new Error(serverErrorMessage(error, "Không từ chối được phát sinh"));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      qc.invalidateQueries({ queryKey: surchargePendingKey });
    },
  });
}

/**
 * Giải quyết yêu cầu xoá đang chờ: `approve=true` xoá mềm dòng (giữ vết),
 * `approve=false` bác yêu cầu — dòng sống tiếp. Cần `order.surcharge.approve`.
 */
export function useResolveSurchargeDelete(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      approve,
      reason,
    }: {
      id: string;
      approve: boolean;
      reason?: string;
    }) => {
      const { error } = await api.POST("/surcharges/{id}/resolve-delete", {
        params: { path: { id } },
        body: { approve, ...(reason ? { reason } : {}) },
      });
      if (error) {
        // Câu của BE mới nói được VÌ SAO bị từ chối; nuốt nó thì người duyệt
        // bấm hoài không hiểu. Cùng khuôn với addSurcharge/deleteSurcharge ở
        // trên — hai hàm liền kề cùng file vốn đã truyền câu BE.
        throw new Error(serverErrorMessage(error, "Không giải quyết được yêu cầu xoá"));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      qc.invalidateQueries({ queryKey: surchargePendingKey });
    },
  });
}

// ══════════ NOTES ══════════

export function useOrderNotes(orderId: string | null) {
  return useQuery({
    queryKey: orderKeys.notes(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/notes", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ghi chú"));
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * Ghi chú của một tấm ảnh (popup Hậu kỳ, QĐ 0438). Máy chủ lọc theo tấm
 * (`?assetId=`): sổ chung chỉ trả 50 ghi chú mới nhất, lọc ở màn là hụt ghi chú
 * cũ của tấm mà không ai biết.
 */
export function useAssetNotes(orderId: string, assetId: string) {
  return useQuery({
    queryKey: orderKeys.assetNotes(orderId, assetId),
    enabled: !!orderId && !!assetId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/notes", {
        params: { path: { id: orderId }, query: { assetId, limit: 200 } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ghi chú của tấm"));
      return data;
    },
    staleTime: 30_000,
  });
}

export function useAddOrderNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      input,
    }: {
      orderId: string;
      input: Omit<OrderNoteInput, "orderId">;
    }) => {
      const { data, error } = await api.POST("/orders/{id}/notes", {
        params: { path: { id: orderId } },
        body: { orderId, ...input },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thêm được ghi chú"));
      return data;
    },
    onSuccess: (created, vars) => {
      // Ghép thẳng ghi chú vừa tạo vào danh sách đang hiện rồi mới làm mới:
      // chờ `invalidateQueries` nghĩa là chờ THÊM một lượt GET nữa mới thấy
      // tin của mình — trên mạng chậm là đúng cái độ trễ làm mục này không
      // "mượt" như một đoạn chat. Máy chủ trả danh sách mới-nhất-trước
      // (`ListNotesByOrder ... ORDER BY created_at DESC`), nên chèn lên ĐẦU.
      // Lượt làm mới ngay sau đó thay nguyên danh sách, nên bản ghép này chỉ
      // sống vài trăm mili-giây và không thể trôi lệch khỏi máy chủ.
      qc.setQueryData<OrderNote[]>(orderKeys.notes(vars.orderId), (cur) =>
        !cur || cur.some((n) => n.id === created.id) ? cur : [created, ...cur],
      );
      qc.invalidateQueries({ queryKey: orderKeys.notes(vars.orderId) });
    },
  });
}

/**
 * Edit a note's text/images. The BE only lets the author (or a cross-user role)
 * through, so call sites should hide the control for other people's notes
 * rather than surfacing a 403.
 */
export function useUpdateOrderNote(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, input }: { noteId: string; input: NoteUpdateInput }) => {
      const { data, error } = await api.PUT("/notes/{noteId}", {
        params: { path: { noteId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không cập nhật được ghi chú"));
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.notes(orderId) });
    },
  });
}

/** Delete a note. Same author-or-privileged rule as useUpdateOrderNote. */
export function useDeleteOrderNote(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await api.DELETE("/notes/{noteId}", {
        params: { path: { noteId } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không xoá được ghi chú"));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.notes(orderId) });
    },
  });
}

/**
 * Bật/tắt một cảm xúc của mình trên ghi chú (QĐ 0256). Lạc quan: chip đổi
 * ngay lúc bấm, máy chủ từ chối thì trả lại như cũ — người bấm "✅ đã làm"
 * chờ một vòng mạng mới thấy dấu là cảm giác nút không ăn.
 */
export function useToggleNoteReaction(orderId: string, myName: string) {
  const qc = useQueryClient();
  const key = orderKeys.notes(orderId);
  return useMutation({
    mutationFn: async ({ noteId, emoji }: { noteId: string; emoji: NoteReactionEmoji }) => {
      const { data, error } = await api.PUT("/notes/{noteId}/reactions", {
        params: { path: { noteId } },
        body: { emoji },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không thả được cảm xúc"));
      return data;
    },
    // Khớp theo TIỀN TỐ: sổ chung và khối "Ghi chú của tấm" (khoá con assetNotes,
    // QĐ 0438) cùng một ghi chú — chỉ sửa khoá sổ chung là chip trong popup đứng im.
    onMutate: async ({ noteId, emoji }) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueriesData<OrderNote[]>({ queryKey: key });
      qc.setQueriesData<OrderNote[]>({ queryKey: key }, (cur) =>
        cur?.map((n) =>
          n.id === noteId ? { ...n, reactions: toggleReaction(n.reactions, emoji, myName) } : n,
        ),
      );
      return { before };
    },
    onError: (_e, _vars, ctx) => {
      for (const [k, data] of ctx?.before ?? []) qc.setQueryData(k, data);
    },
    onSuccess: (reactions, { noteId }) => {
      qc.setQueriesData<OrderNote[]>({ queryKey: key }, (cur) =>
        cur?.map((n) => (n.id === noteId ? { ...n, reactions } : n)),
      );
    },
  });
}

/** Cảm xúc sau khi mình bấm — cùng luật máy chủ: đã thả thì gỡ, chưa thì thêm. Thứ tự cố định. */
export function toggleReaction(
  current: OrderNoteReaction[] | undefined,
  emoji: NoteReactionEmoji,
  myName: string,
): OrderNoteReaction[] {
  const list = current ?? [];
  const hit = list.find((r) => r.emoji === emoji);
  let next: OrderNoteReaction[];
  if (hit?.mine) {
    const idx = hit.names.lastIndexOf(myName);
    const names = idx >= 0 ? hit.names.filter((_, i) => i !== idx) : hit.names.slice(1);
    next = list
      .map((r) => (r.emoji === emoji ? { ...r, mine: false, count: r.count - 1, names } : r))
      .filter((r) => r.count > 0);
  } else if (hit) {
    next = list.map((r) =>
      r.emoji === emoji ? { ...r, mine: true, count: r.count + 1, names: [...r.names, myName] } : r,
    );
  } else {
    next = [...list, { emoji, count: 1, names: [myName], mine: true }];
  }
  return NOTE_REACTIONS.flatMap((e) => next.filter((r) => r.emoji === e));
}

/**
 * Gợi ý @nhắc: người của đơn (sale + ê-kíp) trước, có chữ thì thêm nhân sự
 * khớp tên. Chỉ gọi khi danh sách đang mở.
 */
export function useNoteMentionCandidates(orderId: string, q: string, enabled: boolean) {
  const query = q.trim();
  return useQuery({
    queryKey: orderKeys.noteMentionCandidates(orderId, query),
    enabled: enabled && !!orderId,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET("/orders/{id}/note-mention-candidates", {
        params: { path: { id: orderId }, query: query ? { q: query } : {} },
        signal,
      });
      if (error || !data)
        throw new Error(serverErrorMessage(error, "Không tải được danh sách đồng nghiệp"));
      return data;
    },
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

// ══════════ AUDIT ══════════

export function useOrderAudit(orderId: string | null, limit = 50) {
  return useQuery({
    queryKey: orderKeys.audit(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/audit", {
        params: { query: { entityType: "order", entityId: orderId!, limit } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được audit log"));
      return data;
    },
    staleTime: 60_000,
  });
}

/* ------------------------------------------------------------------ nâng gói
 * HRS-063 (quyết định 0039). Nâng gói là một khoản BÁN THÊM có người hưởng:
 * đơn chuyển hẳn sang gói mới và người tư vấn được ghi công phần tiền chênh.
 *
 * Client KHÔNG gửi số tiền và KHÔNG tự tính chênh — máy chủ tra bảng giá rồi
 * trả về con số. Người bán tự khai doanh số hưởng % của chính mình là hỏng cả
 * cơ chế, nên `PackageUpgradeInput` không có trường tiền nào — chỉ mã gói,
 * kèm mã mục đích/concept khi gói đích có (giá của mục đích là giá đơn, và
 * máy chủ tra nó trong bảng giá chứ không nhận từ đây).
 */

export type PackageUpgrade = components["schemas"]["PackageUpgrade"];
export type PackageOption = components["schemas"]["PackageOption"];

/** Lịch sử nâng gói của đơn — gồm cả dòng đã huỷ khi khách hạ gói lại. */
export function usePackageUpgrades(orderId: string | null) {
  return useQuery({
    queryKey: orderKeys.packageUpgrades(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/package-upgrades", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được lịch sử nâng gói"));
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * Các gói nâng lên được. Danh sách do MÁY CHỦ lọc (chỉ gói đắt hơn) — client
 * không tự lọc: hạ gói không phải lựa chọn bị ẩn cho gọn mắt, nó là việc bị
 * cấm, và luật đó phải sống ở đúng một nơi.
 *
 * `enabled` theo quyền: người không được nâng gói gọi vào sẽ nhận 403, gọi cho
 * tốn round trip rồi hiện lỗi đỏ vô cớ.
 */
export function usePackageUpgradeOptions(orderId: string | null, enabled = true) {
  return useQuery({
    queryKey: orderKeys.packageUpgradeOptions(orderId ?? ""),
    enabled: !!orderId && enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/package-upgrades/options", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách gói"));
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * Nâng đơn lên gói đắt hơn. Trả về đơn SAU khi đổi kèm dòng ghi công, nên UI
 * hiện được đúng số tiền chênh máy chủ vừa tính thay vì đoán lại.
 */
export type UpgradePackageInput = components["schemas"]["PackageUpgradeInput"];

// ---- Đề xuất nâng gói có duyệt (QĐ 0405 · 5a/7a/8a) ----

export type PackageUpgradeRequest = components["schemas"]["PackageUpgradeRequest"];
export type PackageUpgradeRequestInput = components["schemas"]["PackageUpgradeRequestCreateInput"];

/** Khoá hàng chờ duyệt đề xuất nâng gói XUYÊN ĐƠN. */
export const upgradeRequestPendingKey = ["package-upgrade-requests", "pending"] as const;

/** Mọi đề xuất nâng gói của một đơn, mới nhất trước. */
export function usePackageUpgradeRequests(orderId: string | null) {
  return useQuery({
    queryKey: orderKeys.packageUpgradeRequests(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/package-upgrade-requests", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đề xuất nâng gói"));
      return data.items;
    },
    staleTime: 15_000,
  });
}

/** Hàng chờ duyệt của người xem (máy chủ lọc theo quyền duyệt + cơ sở). */
export function usePendingPackageUpgradeRequests(enabled: boolean) {
  return useQuery({
    queryKey: upgradeRequestPendingKey,
    enabled,
    queryFn: async () => {
      const { data, error } = await api.GET("/package-upgrade-requests/pending");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đề xuất chờ duyệt"));
      return data.items;
    },
    refetchInterval: enabled ? 60_000 : false,
    staleTime: 20_000,
  });
}

function useInvalidateUpgradeRequests() {
  const qc = useQueryClient();
  return (orderId: string) => {
    void qc.invalidateQueries({ queryKey: orderKeys.packageUpgradeRequests(orderId) });
    void qc.invalidateQueries({ queryKey: upgradeRequestPendingKey });
    void qc.invalidateQueries({ queryKey: orderKeys.packageUpgrades(orderId) });
    void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
  };
}

/** CSKH lập đề xuất nâng gói (chờ người có quyền duyệt). */
export function useCreatePackageUpgradeRequest(orderId: string) {
  const refresh = useInvalidateUpgradeRequests();
  return useMutation({
    mutationFn: async (body: PackageUpgradeRequestInput) => {
      const { data, error } = await api.POST("/orders/{id}/package-upgrade-requests", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gửi được đề xuất nâng gói"));
      return data;
    },
    onSuccess: () => refresh(orderId),
  });
}

export type UpgradeRequestDecision =
  | { kind: "approve"; request: PackageUpgradeRequest }
  | { kind: "reject"; request: PackageUpgradeRequest; reason: string }
  | { kind: "cancel"; request: PackageUpgradeRequest };

/** Duyệt / từ chối / rút lại một đề xuất. */
export function useDecidePackageUpgradeRequest() {
  const refresh = useInvalidateUpgradeRequests();
  return useMutation({
    mutationFn: async (d: UpgradeRequestDecision) => {
      const path = { params: { path: { requestId: d.request.id } } };
      const res =
        d.kind === "approve"
          ? await api.POST("/package-upgrade-requests/{requestId}/approve", path)
          : d.kind === "reject"
            ? await api.POST("/package-upgrade-requests/{requestId}/reject", { ...path, body: { reason: d.reason } })
            : await api.POST("/package-upgrade-requests/{requestId}/cancel", path);
      if (res.error || !res.data) {
        const fallback =
          d.kind === "approve" ? "Không duyệt được" : d.kind === "reject" ? "Không từ chối được" : "Không rút lại được";
        throw new Error(serverErrorMessage(res.error, fallback));
      }
      return res.data;
    },
    onSuccess: (_r, d) => refresh(d.request.orderId),
  });
}

export function useUpgradePackage(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpgradePackageInput) => {
      const { data, error } = await api.POST("/orders/{id}/package-upgrades", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) {
        // Nổi nguyên văn message BE (khuôn useAddSurcharge): "chỉ nâng lên gói
        // đắt hơn" là câu người dùng cần đọc, nuốt đi thành "Lỗi" là bắt họ
        // đoán mình vừa làm sai chỗ nào.
        throw new Error(saleLineErrorMessage(error, "Không nâng gói được"));
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.packageUpgrades(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.packageUpgradeOptions(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
    },
  });
}
